'use client';

import React from 'react';
import {
  getDeliveryLabel,
  M2HKind,
  OVERNIGHT_DELIVERY,
  P2RKind,
  PRIORITY_DELIVERY,
  STANDARD_DELIVERY,
} from 'helpers/constants/cart';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { getCaSubRateTaxes } from 'helpers/utils/caTax';
import { classnames } from 'helpers/utils/classnames';
import { isEURegion } from 'helpers/utils/util';
import { CA_LOCALE } from 'project.config';
import { useFeatures } from 'providers/feature-flag';
import { CouponDiscounts } from 'types/entity/cart';
import { Transaction } from 'frontastic/hooks/useCart/types';
import { Money } from '../../../../../../types/product';

interface DiscountType {
  couponCode?: string;
  discountAmount?: Money;
  discountId?: string;
  target?: any;
  description?: string;
}
interface Props {
  cartKind: string;
  locale: string;
  transactions: Transaction;
  itemsCount: number;
  orderSummary: string;
  subTotalSubHeading: string;
  subTotalItems: string;
  items: string;
  discount: string;
  shippingLabel: string;
  taxLabel: string;
  vatForPhotobookLabel: string;
  vatForOtherProductsLabel: string;
  vatForShippingLabel: string;
  total: string;
  taxCalulatedLabel: string;
  totalSavingsLabel: string;
  discountApplieadLabel: string;
  discounts: DiscountType[];
  discountsContainerClassName: string;
  sectionMode: 'p2rCart' | 'm2hCart';
  paymentMethod: string;
  whoIsPickingUpTheOrder: string;
  payAtStore: string;
  orderDetailsData?: any;
  freeSummary?: string;
  pickupFirstName: string;
  pickupLastName: string;
  productSaleLabel: string;
}

const OrderSummary: React.FC<Props> = ({
  cartKind,
  locale,
  transactions,
  itemsCount,
  orderSummary,
  subTotalSubHeading,
  subTotalItems,
  items,
  discount,
  shippingLabel,
  taxLabel,
  vatForPhotobookLabel,
  vatForOtherProductsLabel,
  vatForShippingLabel,
  total,
  taxCalulatedLabel,
  totalSavingsLabel,
  discountApplieadLabel,
  discounts,
  discountsContainerClassName,
  sectionMode,
  paymentMethod,
  whoIsPickingUpTheOrder,
  payAtStore,
  orderDetailsData,
  freeSummary,
  pickupFirstName,
  pickupLastName,
  productSaleLabel,
}) => {
  const { enableCartDiscountChanges } = useFeatures();

  const totalDiscount = transactions.allDiscounts;

  const photoBookTax = orderDetailsData?.taxed?.taxBreakdown?.photoBooksAndExtraPages;
  const otherProductsTax = orderDetailsData?.taxed?.taxBreakdown?.otherProducts;
  const shippingTax = orderDetailsData?.taxed?.taxBreakdown?.shipping;

  const isPhotoBookTaxApplicable = Boolean(
    photoBookTax?.taxAmount?.centAmount && photoBookTax.taxAmount.centAmount > 0,
  );
  const isOtherProductsTaxApplicable = Boolean(
    otherProductsTax?.taxAmount?.centAmount && otherProductsTax.taxAmount.centAmount > 0,
  );
  const isShippingTaxApplicable = Boolean(shippingTax?.taxAmount?.centAmount && shippingTax.taxAmount.centAmount > 0);
  const orderTotal = Number(orderDetailsData?.sum?.centAmount ?? 0) / 100;
  const shippingMethodName = getDeliveryLabel(orderDetailsData?.shippingInfo?.name as string);
  const showShippingAsterisk =
    shippingMethodName?.toLowerCase() === STANDARD_DELIVERY?.toLowerCase() &&
    shippingMethodName?.toLowerCase() !== PRIORITY_DELIVERY?.toLowerCase() &&
    shippingMethodName?.toLowerCase() !== OVERNIGHT_DELIVERY?.toLowerCase() &&
    orderTotal < 100;

  return (
    <div className="mt-16 lg:mt-120 lg:block lg:w-1/4" data-ffid="ctr-summary-root">
      <div className="rounded-xl border border-[#EBEBEB] bg-white p-16" data-ffid="ctr-summary-card">
        <p className="text-14 font-semibold text-gray-800 lg:text-16" data-ffid="lbl-summary-title">
          {orderSummary}
        </p>

        <div className="text-14 lg:text-16" data-ffid="ctr-summary-values">
          <div className="flex justify-between" data-ffid="ctr-summary-subtotal">
            <span data-ffid="lbl-summary-subtotal-label">
              {enableCartDiscountChanges()
                ? `${items} (${itemsCount})`
                : `${subTotalSubHeading} (${itemsCount}) ${subTotalItems}`}
            </span>
            <span data-ffid="lbl-summary-subtotal-value">
              {transactions?.subtotal?.centAmount
                ? CurrencyHelpers.formatForCurrency(transactions?.subtotal, locale)
                : '0.00'}
            </span>
          </div>

          {cartKind === M2HKind && (
            <>
              {enableCartDiscountChanges() ? (
                <>
                  {/* Product Sale Label */}
                  {totalDiscount?.salePriceDiscount?.centAmount && totalDiscount?.salePriceDiscount?.centAmount > 0 ? (
                    <div className="flex justify-between" data-ffid="ctr-summary-product-sale">
                      <span
                        className="mt-4 text-sm text-[#018463] lg:text-base"
                        data-ffid="lbl-summary-product-sale"
                        data-testid="product-sale-label"
                      >
                        {productSaleLabel}
                      </span>
                      <span
                        data-ffid="lbl-summary-product-sale-val"
                        data-testid="product-sale-value"
                        className={classnames('mt-4 justify-self-end text-right text-sm text-[#018463] lg:text-base')}
                      >
                        {` - ${CurrencyHelpers.formatForCurrency(totalDiscount.salePriceDiscount, locale)}`}
                      </span>
                    </div>
                  ) : null}

                  {/* Coupon Discounts */}
                  {totalDiscount?.couponDiscounts?.map((couponDiscount: CouponDiscounts, idx) => (
                    <div
                      key={couponDiscount.discountId}
                      className="flex justify-between"
                      data-ffid={`ctr-summary-coupon-discount-${idx + 1}`}
                    >
                      <span
                        className="mt-4 text-sm text-[#018463] lg:text-base"
                        data-ffid={`lbl-summary-coupon-discount-label-${idx + 1}`}
                        data-testid={`coupon-discount-label-${idx + 1}`}
                      >
                        {couponDiscount.couponCode}
                      </span>
                      <span className="justify-self-end" />
                      <span
                        data-ffid={`lbl-summary-coupon-discount-val-${idx + 1}`}
                        data-testid={`coupon-discount-value-${idx + 1}`}
                        className={classnames('mt-4 justify-self-end text-right text-sm text-[#018463] lg:text-base')}
                      >
                        {` - ${CurrencyHelpers.formatForCurrency(couponDiscount.discountAmount, locale)}`}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {/* Discount with (Coupon length) */}
                  {discounts &&
                    discounts.length > 0 &&
                    transactions.couponDiscount &&
                    transactions.couponDiscount?.centAmount > 0 && (
                      <div className="flex justify-between" data-ffid="ctr-summary-discount">
                        <span data-ffid="lbl-summary-discount-label">{`${discount} (${discounts?.length})`}</span>
                        <span className="text-[#018463]" data-ffid="lbl-summary-discount-value">
                          {transactions?.discount
                            ? `- ${CurrencyHelpers.formatForCurrency(transactions?.couponDiscount, locale)}`
                            : '0.00'}
                        </span>
                      </div>
                    )}
                </>
              )}
              {/* Shipping */}
              <div className="flex justify-between" data-ffid="ctr-summary-shipping">
                <span data-ffid="lbl-summary-shipping-label">
                  {`${shippingLabel}${showShippingAsterisk ? '*' : ''}`}
                </span>
                <span className="flex min-w-0 flex-row items-center justify-end gap-x-30">
                  {transactions.isShippingDiscounted ? (
                    <span
                      data-ffid="lbl-summary-shipping-strikeval"
                      data-testid="shipping-strike-price"
                      className="text-right text-sm text-[#5C5C5C] line-through lg:text-base"
                    >
                      {CurrencyHelpers.formatForCurrency(
                        orderDetailsData.shippingInfo?.rate?.price ?? { centAmount: 0 },
                        locale,
                      )}
                    </span>
                  ) : (
                    <span className="justify-self-end" data-testid="shipping-spacer" />
                  )}
                  <span
                    className="justify-self-end text-right text-sm lg:text-base"
                    data-ffid="lbl-summary-shipping-val"
                    data-testid="shipping-value"
                  >
                    {/* If shipping is free, show "Free", else either discounted or full price */}
                    {(transactions.isShippingCalculated || transactions.isShippingDiscounted) &&
                    transactions.shipping.centAmount === 0
                      ? `${freeSummary}`
                      : CurrencyHelpers.formatForCurrency(transactions?.shipping, locale)}
                  </span>
                </span>
              </div>
            </>
          )}

          <div className="flex justify-between" data-ffid="ctr-summary-tax">
            <span data-ffid="lbl-summary-tax-label">{taxLabel}</span>
            <span data-ffid="lbl-summary-tax-value">
              {transactions?.tax?.centAmount ? CurrencyHelpers.formatForCurrency(transactions?.tax, locale) : '0.00'}
            </span>
          </div>

          {isEURegion(orderDetailsData?.storeKey) && (
            <>
              {isPhotoBookTaxApplicable && (
                <div className="flex justify-between" data-ffid="ctr-summary-tax-eu-photobook">
                  <span
                    className="text-sm"
                    data-ffid="lbl-summary-tax-eu-photobook"
                    data-testid="eu-photobook-tax-label"
                  >
                    {vatForPhotobookLabel}{' '}
                    {photoBookTax?.taxRate?.amount
                      ? `${Number.parseFloat((photoBookTax.taxRate.amount * 100).toFixed(2))}%`
                      : ''}
                  </span>
                  <span className="justify-self-end" data-testid="tax-spacer" />
                  <span
                    className="justify-self-end text-right text-sm"
                    data-ffid="lbl-summary-tax-eu-photobook-val"
                    data-testid="eu-photobook-tax-value"
                  >
                    {CurrencyHelpers.formatForCurrency(photoBookTax?.taxAmount ?? 0, locale)}
                  </span>
                </div>
              )}

              {isOtherProductsTaxApplicable && (
                <div className="flex justify-between" data-ffid="ctr-summary-tax-eu-other-products">
                  <span
                    className="text-sm"
                    data-ffid="lbl-summary-tax-eu-other-products"
                    data-testid="eu-other-products-tax-label"
                  >
                    {vatForOtherProductsLabel}{' '}
                    {otherProductsTax?.taxRate?.amount
                      ? `${Number.parseFloat((otherProductsTax.taxRate?.amount * 100).toFixed(2))}%`
                      : ''}
                  </span>
                  <span className="justify-self-end" data-testid="tax-spacer" />
                  <span
                    className="justify-self-end text-right text-sm"
                    data-ffid="lbl-summary-tax-eu-other-products-val"
                    data-testid="eu-other-products-tax-value"
                  >
                    {CurrencyHelpers.formatForCurrency(otherProductsTax?.taxAmount ?? 0, locale)}
                  </span>
                </div>
              )}

              {isShippingTaxApplicable && (
                <div className="flex justify-between" data-ffid="ctr-summary-tax-eu-shipping">
                  <span className="text-sm" data-ffid="lbl-summary-tax-eu-shipping" data-testid="eu-shipping-tax-label">
                    {vatForShippingLabel}{' '}
                    {shippingTax?.taxRate?.amount
                      ? `${Number.parseFloat((shippingTax.taxRate?.amount * 100).toFixed(2))}%`
                      : ''}
                  </span>
                  <span className="justify-self-end" data-testid="tax-spacer" />
                  <span
                    className="justify-self-end text-right text-sm"
                    data-ffid="lbl-summary-tax-eu-shipping-val"
                    data-testid="eu-shipping-tax-value"
                  >
                    {CurrencyHelpers.formatForCurrency(shippingTax?.taxAmount ?? 0, locale)}
                  </span>
                </div>
              )}
            </>
          )}

          {orderDetailsData?.shippingAddress?.country?.toLowerCase() === CA_LOCALE.toLowerCase() && (
            <>
              {getCaSubRateTaxes(orderDetailsData?.lineItems, orderDetailsData?.shippingInfo?.taxRate?.subRates).map(
                ({ taxName, taxPercent, totalTaxAmount }, index) => (
                  <div
                    key={`tax-${taxName}`}
                    className="flex justify-between text-sm text-[#5C5C5C] lg:text-base"
                    data-ffid={`ctr-summary-tax-ca-${index}`}
                  >
                    <span data-ffid={`lbl-summary-tax-ca-${index}`} data-testid="tax-label">
                      {`${taxName} (${taxPercent.toFixed(2)}%)`}
                    </span>
                    <span className="justify-self-end" data-testid="tax-spacer" />
                    <span
                      className="justify-self-end text-right"
                      data-ffid={`lbl-tax-amount-ca-${index}`}
                      data-testid="tax-value"
                    >
                      {CurrencyHelpers.formatForCurrency(
                        {
                          centAmount: Math.round(totalTaxAmount * 100),
                          currencyCode: transactions?.tax?.currencyCode ?? 'CAD',
                          fractionDigits: transactions?.tax?.fractionDigits ?? 2,
                        },
                        locale,
                      )}
                    </span>
                  </div>
                ),
              )}
            </>
          )}

          <div className="flex justify-between font-semibold" data-ffid="ctr-summary-total">
            <span data-ffid="lbl-summary-total-label">{total}</span>
            <span data-ffid="lbl-summary-total-value">
              {transactions?.total?.centAmount
                ? CurrencyHelpers.formatForCurrency(transactions?.total, locale)
                : '0.00'}
            </span>
          </div>
        </div>

        {cartKind === P2RKind && (
          <span className="mb-16 mt-8 text-12" data-ffid="lbl-summary-tax-message">
            {taxCalulatedLabel}
          </span>
        )}

        {cartKind == M2HKind && discounts && discounts.length > 0 && (
          <div className={discountsContainerClassName} data-ffid="ctr-summary-discount-list">
            <p className="mb-3 text-base font-bold" data-ffid="lbl-summary-discount-heading">
              {discountApplieadLabel ?? 'Discount Applied'}
            </p>

            <div className="rounded-md border border-[#EBEBEB] bg-white p-8" data-ffid="ctr-summary-discount-items">
              {discounts.map((discount, index) => {
                const code = discount.couponCode;
                const description = discount.description;

                return (
                  <div
                    key={discount.discountId}
                    className="relative mb-6 last:mb-0"
                    data-ffid={`ctr-summary-discount-item-${index}`}
                  >
                    <h4 className="pr-24 text-lg text-gray-900" data-ffid={`lbl-summary-discount-code-${index}`}>
                      {code}
                    </h4>

                    {description && (
                      <p
                        className="mt-2 pr-10 text-xs leading-relaxed text-gray-600"
                        data-ffid={`lbl-summary-discount-description-${index}`}
                      >
                        {description}
                      </p>
                    )}

                    {index < discounts.length - 1 && (
                      <div
                        className="mt-4 border-b border-[#EBEBEB]"
                        data-ffid={`ctr-summary-discount-divider-${index}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {transactions?.totalSavings?.centAmount !== undefined &&
          transactions?.totalSavings?.centAmount > 0 &&
          (enableCartDiscountChanges() ? (
            <div
              className="mt-16 flex justify-between rounded-lg border border-solid border-[#018463] bg-[#018463] px-12 py-8 lg:mt-16"
              data-ffid="ctr-summary-savings"
            >
              <span data-ffid="lbl-summary-savings-label" className="text-sm text-white lg:text-base">
                {totalSavingsLabel}
              </span>
              <span
                data-ffid="lbl-summary-savings-value"
                className="justify-self-end text-right text-sm text-white lg:text-base"
              >
                {transactions?.totalSavings?.centAmount > 0
                  ? CurrencyHelpers.formatForCurrency(transactions?.totalSavings, locale)
                  : '0.00'}
              </span>
            </div>
          ) : (
            <div
              className="mt-16 flex justify-between rounded-lg border-2 border-green-800 p-10 text-16 text-[#018463]"
              data-ffid="ctr-summary-savings"
            >
              <span data-ffid="lbl-summary-savings-label">{totalSavingsLabel}</span>
              <span data-ffid="lbl-summary-savings-value">
                {transactions?.totalSavings?.centAmount > 0
                  ? CurrencyHelpers.formatForCurrency(transactions?.totalSavings, locale)
                  : '0.00'}
              </span>
            </div>
          ))}
      </div>

      {sectionMode === P2RKind && (
        <div className="my-24 flex flex-row gap-12 lg:flex-col lg:gap-24" data-ffid="ctr-summary-pickup">
          <div className="flex flex-1 flex-col rounded-lg border border-[#EBEBEB] p-16" data-ffid="ctr-summary-payment">
            <span className="text-14 font-semibold text-gray-800 lg:text-16" data-ffid="lbl-summary-payment-title">
              {paymentMethod}
            </span>
            <span className="text-14 text-gray-700 lg:text-16" data-ffid="lbl-summary-payment-value">
              {payAtStore}
            </span>
          </div>

          <div
            className="flex flex-1 flex-col rounded-lg border border-[#EBEBEB] p-16"
            data-ffid="ctr-summary-pickup-person"
          >
            <span
              className="text-14 font-semibold text-gray-800 lg:text-16"
              data-ffid="lbl-summary-pickup-person-title"
            >
              {whoIsPickingUpTheOrder}
            </span>
            <span className="text-14 text-gray-700 lg:text-16" data-ffid="lbl-summary-pickup-person-value">
              {pickupFirstName} {pickupLastName}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;
